import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FaEdit, FaPlus } from 'react-icons/fa';
import jwt_decode from 'jwt-decode';
import AiPromptConfigApi from '../../../api/AiPromptConfigApi';
import { fetchAiChatbotIndexingOptions } from '../../../utils/aiChatbotIndexingMode';

const EXAMPLE_PROMPTS = [
  { key: 'sql_platform', name: 'SQL Platform Query Generator' },
  { key: 'document_transform', name: 'Document Transform' },
  { key: 'email_generation', name: 'Email Generation' },
  { key: 'whatsapp_generation', name: 'WhatsApp Generation' },
];

const emptyForm = () => ({
  prompt_key: '',
  prompt_name: '',
  content: '',
  is_active: true,
});

const AiPromptConfigPage = () => {
  const isAdmin = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const decoded = jwt_decode(token);
      return String(decoded?.role_name || '').toUpperCase() === 'ADMIN';
    } catch {
      return false;
    }
  }, []);

  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ variant: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const opts = await fetchAiChatbotIndexingOptions();
        if (active) setOptions(opts);
      } catch {
        if (active) setOptions({ canIndexStandard: false });
      } finally {
        if (active) setLoadingOptions(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const loadPrompts = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await AiPromptConfigApi.list();
      if (!res?.success) {
        throw new Error(res?.message || 'Failed to load platform prompts.');
      }
      setPrompts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setMessage({ variant: 'danger', text: err.message || 'Failed to load platform prompts.' });
      if (!silent) setPrompts([]);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (options?.canIndexStandard) {
      loadPrompts();
    } else if (!loadingOptions) {
      setLoading(false);
    }
  }, [options?.canIndexStandard, loadingOptions, loadPrompts]);

  const openCreate = () => {
    setEditingKey(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingKey(row.prompt_key);
    setForm({
      prompt_key: row.prompt_key,
      prompt_name: row.prompt_name,
      content: row.content,
      is_active: row.is_active !== false,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingKey(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ variant: '', text: '' });
    const isEdit = Boolean(editingKey);
    try {
      const payload = {
        prompt_key: form.prompt_key.trim().toLowerCase(),
        prompt_name: form.prompt_name.trim(),
        content: form.content.trim(),
        is_active: form.is_active === true,
      };
      const res = isEdit
        ? await AiPromptConfigApi.update(editingKey, {
            prompt_name: payload.prompt_name,
            content: payload.content,
            is_active: payload.is_active,
          })
        : await AiPromptConfigApi.create(payload);
      if (!res?.success) {
        throw new Error(res?.message || 'Save failed.');
      }
      setSaving(false);
      setShowModal(false);
      setEditingKey(null);
      setForm(emptyForm());
      setMessage({
        variant: 'success',
        text: isEdit ? 'Platform prompt updated.' : 'Platform prompt created.',
      });
      await loadPrompts({ silent: true });
    } catch (err) {
      setMessage({ variant: 'danger', text: err.message || 'Save failed.' });
      setSaving(false);
    }
  };

  const handleToggleActive = async (row) => {
    try {
      const res = await AiPromptConfigApi.update(row.prompt_key, {
        prompt_name: row.prompt_name,
        content: row.content,
        is_active: !row.is_active,
      });
      if (!res?.success) {
        throw new Error(res?.message || 'Update failed.');
      }
      await loadPrompts({ silent: true });
    } catch (err) {
      setMessage({ variant: 'danger', text: err.message || 'Update failed.' });
    }
  };

  if (!isAdmin) {
    return <Alert variant="warning">Only administrators can manage platform AI prompts.</Alert>;
  }

  if (loadingOptions) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading…
      </div>
    );
  }

  if (!options?.canIndexStandard) {
    return (
      <Alert variant="info">
        Platform prompt management is only available for product builder tenants.
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 className="mb-1">Manage Platform Prompts</h5>
          <p className="text-muted small mb-0">
            Edit shared system prompts used across all tenants. Changes apply to new AI requests
            immediately (cached for up to 5 minutes).
          </p>
        </div>
        <Button variant="primary" onClick={openCreate} disabled={loading || refreshing}>
          <FaPlus className="me-1" />
          Add prompt
        </Button>
      </div>

      {message.text && (
        <Alert
          variant={message.variant || 'info'}
          dismissible
          onClose={() => setMessage({ variant: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading platform prompts…
        </div>
      ) : prompts.length === 0 ? (
        <Alert variant="light" className="border">
          <p className="mb-2">No platform prompts found. Common keys:</p>
          <ul className="small mb-3">
            {EXAMPLE_PROMPTS.map((ex) => (
              <li key={ex.key}>
                <code>{ex.key}</code> — {ex.name}
              </li>
            ))}
          </ul>
          <Button variant="outline-primary" size="sm" onClick={openCreate}>
            Add your first prompt
          </Button>
        </Alert>
      ) : (
        <div className="position-relative">
          {refreshing && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
              style={{ zIndex: 2 }}
            >
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="small text-muted">Updating…</span>
            </div>
          )}
          <Table responsive striped bordered hover className="bg-white mb-0">
            <thead>
              <tr>
                <th>Key</th>
                <th>Name</th>
                <th style={{ width: 80 }}>Version</th>
                <th style={{ width: 90 }}>Active</th>
                <th style={{ width: 70 }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {prompts.map((row) => (
                <tr key={row.id || row.prompt_key}>
                  <td>
                    <code>{row.prompt_key}</code>
                  </td>
                  <td>{row.prompt_name}</td>
                  <td>
                    <Badge bg="secondary">v{row.version || 1}</Badge>
                  </td>
                  <td>
                    <Form.Check
                      type="switch"
                      id={`prompt-active-${row.prompt_key}`}
                      checked={row.is_active !== false}
                      disabled={refreshing}
                      onChange={() => handleToggleActive(row)}
                      label=""
                      aria-label={`Active ${row.prompt_key}`}
                    />
                  </td>
                  <td>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      title="Edit"
                      disabled={refreshing}
                      onClick={() => openEdit(row)}
                    >
                      <FaEdit />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={closeModal}
        backdrop={saving ? 'static' : true}
        keyboard={!saving}
        size="lg"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editingKey ? 'Edit platform prompt' : 'Add platform prompt'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Prompt key</Form.Label>
            <Form.Control
              type="text"
              value={form.prompt_key}
              onChange={(e) => setForm((prev) => ({ ...prev, prompt_key: e.target.value }))}
              placeholder="e.g. sql_platform"
              disabled={saving || Boolean(editingKey)}
            />
            <Form.Text className="text-muted">
              Stable identifier used in code. Lowercase letters, numbers, and underscores only.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Display name</Form.Label>
            <Form.Control
              type="text"
              value={form.prompt_name}
              onChange={(e) => setForm((prev) => ({ ...prev, prompt_name: e.target.value }))}
              placeholder="e.g. SQL Platform Query Generator"
              disabled={saving}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Prompt content</Form.Label>
            <Form.Control
              as="textarea"
              rows={14}
              value={form.content}
              onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
              placeholder="System prompt text sent to the LLM…"
              disabled={saving}
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
            <Form.Text className="text-muted">
              Content changes create a new version. Name and active status update in place.
            </Form.Text>
          </Form.Group>
          <Form.Check
            type="switch"
            id="prompt-form-active"
            label="Active"
            checked={form.is_active === true}
            onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            disabled={saving}
          />
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end gap-2">
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={
              saving
              || !form.prompt_key.trim()
              || !form.prompt_name.trim()
              || !form.content.trim()
            }
            className="ai-dictionary-save-btn"
          >
            {saving ? (
              <span className="d-inline-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" role="status" />
                Saving…
              </span>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AiPromptConfigPage;
